import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

const FIREBASE_ENV_KEYS = {
    apiKey: "REACT_APP_FIREBASE_API_KEY",
    authDomain: "REACT_APP_FIREBASE_AUTH_DOMAIN",
    projectId: "REACT_APP_FIREBASE_PROJECT_ID",
    storageBucket: "REACT_APP_FIREBASE_STORAGE_BUCKET",
    messagingSenderId: "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
    appId: "REACT_APP_FIREBASE_APP_ID",
} as const;

type FirebaseConfigKey = keyof typeof FIREBASE_ENV_KEYS;

const readFirebaseEnv = (key: FirebaseConfigKey): string => process.env[FIREBASE_ENV_KEYS[key]]?.trim() ?? "";

const isPlaceholder = (value: string): boolean => {
    const normalized = value.toLowerCase();
    return normalized.includes("replace_me") || normalized.includes("your_");
};

const firebaseConfig = {
    apiKey: readFirebaseEnv("apiKey"),
    authDomain: readFirebaseEnv("authDomain"),
    projectId: readFirebaseEnv("projectId"),
    storageBucket: readFirebaseEnv("storageBucket"),
    messagingSenderId: readFirebaseEnv("messagingSenderId"),
    appId: readFirebaseEnv("appId"),
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID?.trim(),
};

const missingFirebaseEnv = (Object.keys(FIREBASE_ENV_KEYS) as FirebaseConfigKey[]).filter(
    (key) => !firebaseConfig[key] || isPlaceholder(firebaseConfig[key])
);

export const firebaseConfigStatus = {
    configured: missingFirebaseEnv.length === 0,
    missingEnvKeys: missingFirebaseEnv.map((key) => FIREBASE_ENV_KEYS[key]),
} as const;

if (!firebaseConfigStatus.configured) {
    console.warn(
        "[firebase] Missing required Firebase env vars. Firebase-dependent features are disabled until configured:",
        firebaseConfigStatus.missingEnvKeys.join(", ")
    );
}

const app = firebaseConfigStatus.configured ? initializeApp(firebaseConfig) : null;
export const auth = app ? getAuth(app) : null;
const db = app ? getFirestore(app) : null;

const requireFirestore = () => {
    if (!db) {
        throw new Error(
            `Firebase is not configured. Missing env vars: ${firebaseConfigStatus.missingEnvKeys.join(", ")}`
        );
    }
    return db;
};

export const addToWaitlist = async (email: string, name: string = '') => {
    try {
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Request timed out")), 10000)
        );
        const firestore = requireFirestore();

        const docRef = await Promise.race([
            addDoc(collection(firestore, "waitlist"), {
                email,
                name,
                createdAt: serverTimestamp(),
                source: 'web'
            }),
            timeoutPromise
        ]) as any; // Type assertion since docRef is from the race

        return { success: true, id: docRef.id };
    } catch (e) {
        console.error("Error adding document: ", e);
        return { success: false, error: e };
    }
};

export interface ContactFormData {
    name: string;
    email: string;
    company?: string;
    phone?: string;
    message: string;
}

export const submitContactForm = async (data: ContactFormData) => {
    try {
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Request timed out")), 10000)
        );
        const firestore = requireFirestore();

        const docRef = await Promise.race([
            addDoc(collection(firestore, "contacts"), {
                ...data,
                createdAt: serverTimestamp(),
                source: 'web'
            }),
            timeoutPromise
        ]) as any;

        return { success: true, id: docRef.id };
    } catch (e) {
        console.error("Error submitting contact form: ", e);
        return { success: false, error: e };
    }
};

export default app;
